import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * The binary consent state that gates all tracking.
 */
export type ConsentState = 'granted' | 'denied';

/**
 * Holds and broadcasts the current Consent_State.
 *
 * Default is `granted` (Req 1.3 — absent consent is assumed to be already
 * obtained by the landing page).
 *
 * Not providedIn root — provided per embed instance in main.embed.ts.
 */
@Injectable()
export class ConsentService {
  /** Recorded indications for unrecognised consent values (Req 1.7). */
  readonly indications: string[] = [];

  private readonly _state$ = new BehaviorSubject<ConsentState>('granted');

  /** Observable stream of consent state changes. */
  readonly state$: Observable<ConsentState> = this._state$.asObservable();

  /** Synchronous read of the current consent state. */
  get state(): ConsentState {
    return this._state$.getValue();
  }

  /**
   * Set the consent state from an external value.
   *
   * Mapping rules (per Requirements 1.3, 1.7):
   * - `'granted'` → `'granted'`
   * - `'denied'`  → `'denied'`
   * - `null`, `undefined`, or absent → `'granted'` (Req 1.3)
   * - Any other string → `'granted'` + recorded indication (Req 1.7)
   */
  set(value: string | null | undefined): void {
    if (value === 'granted') {
      this._state$.next('granted');
      return;
    }

    if (value === 'denied') {
      this._state$.next('denied');
      return;
    }

    if (value == null || value === '') {
      // Absent / omitted — consent assumed granted (Req 1.3)
      this._state$.next('granted');
      return;
    }

    // Unrecognised value — treat as granted + record indication (Req 1.7)
    const indication = `Unrecognised consent value "${value}" — treating as granted.`;
    this.indications.push(indication);
    console.warn('[ConsentService]', indication);
    this._state$.next('granted');
  }
}
